// app/pages/RolesPermissions.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Save } from 'lucide-react';
import { userService } from '../services/user.service';
import RolesList from '../components/roles/RolesList';
import PermissionsGrid from '../components/roles/PermissionsGrid';
import RoleModal from '../components/roles/RoleModal';
import LoadingSpinner from '../components/common/LoadingSpinner';

const RolesPermissions = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [rolePermissions, setRolePermissions] = useState([]);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setHasChanges(false);
    }, [selectedRole]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [rolesData, permsData] = await Promise.all([
                userService.getRoles(),
                userService.getPermissions()
            ]);
            setRoles(rolesData.items || []);
            setPermissions(permsData.items || []);

            if (rolesData.items && rolesData.items.length > 0 && !selectedRole) {
                selectRole(rolesData.items[0]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const selectRole = async (role) => {
        setSelectedRole(role);
        try {
            const data = await userService.getRolePermissions(role.id);
            setRolePermissions(data.permissions || []);
        } catch (error) {
            console.error('Error fetching role permissions:', error);
        }
    };

    const handlePermissionToggle = (permissionId) => {
        setRolePermissions(prev => {
            if (prev.includes(permissionId)) {
                return prev.filter(id => id !== permissionId);
            } else {
                return [...prev, permissionId];
            }
        });
        setHasChanges(true);
    };

    const savePermissions = async () => {
        if (!selectedRole) return;

        setSaving(true);
        try {
            await userService.updateRolePermissions(selectedRole.id, rolePermissions);
            setHasChanges(false);
            // Show success message
        } catch (error) {
            console.error('Error saving permissions:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleCreateRole = () => {
        setEditingRole(null);
        setShowRoleModal(true);
    };

    const handleEditRole = (role) => {
        setEditingRole(role);
        setShowRoleModal(true);
    };

    const handleSaveRole = async (roleData) => {
        try {
            if (editingRole) {
                await userService.updateRole(editingRole.id, roleData);
            } else {
                await userService.createRole(roleData);
            }
            setShowRoleModal(false);
            fetchData();
        } catch (error) {
            console.error('Error saving role:', error);
            throw error;
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="space-y-6">
            <div className="bg-white flex justify-between items-center bxshadowhd p-3 mrgall border">
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                    
                    Roles & Permissions
                </h1>
                <button
                    onClick={handleCreateRole}
                    className="flex items-center gap-2 px-4 py-2 bg-scarlet-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Role
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mrgall ">
                <div className="lg:col-span-1">
                    <RolesList
                        roles={roles}
                        selectedRole={selectedRole}
                        onSelectRole={selectRole}
                        onEditRole={handleEditRole}
                    />
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg bxshadowhd border">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900">
                                {selectedRole ? `Permissions for ${selectedRole.role_name}` : 'Select a role'}
                            </h3>
                            {selectedRole && hasChanges && (
                                <button
                                    onClick={savePermissions}
                                    disabled={saving}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                >
                                    <Save className="w-4 h-4" />
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            )}
                        </div>

                        <PermissionsGrid
                            permissions={permissions}
                            rolePermissions={rolePermissions}
                            onToggle={handlePermissionToggle}
                            disabled={!selectedRole}
                        />
                    </div>
                </div>
            </div>

            <RoleModal
                isOpen={showRoleModal}
                onClose={() => setShowRoleModal(false)}
                onSave={handleSaveRole}
                role={editingRole}
            />
        </div>
    );
};

export default RolesPermissions;